import { takesMoney } from "../utils/takesMoney";
import React, { useRef } from "react";
import { FaInstagram } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { openExternal } from "../lib/openExternal";
import FanPageMenu from "./FanPageMenu";
import SaveTalentHeart from "./SaveTalentHeart";
import { defaultPriceFor } from "../utils/payinConfig";
import type { TipCheckout } from "../hooks/useTipCheckout";
import type { BuyCheckout } from "../hooks/useBuyCheckout";
import type { SongRequest } from "../hooks/useSongRequest";
import { t } from "../i18n";
import { T } from "../i18n/T";
import { DEFAULT_AVATAR_SRC, showDefaultAvatar } from "../lib/defaultAvatar";
import { useFitText } from "../hooks/useFitText";
import "./FanPageHeader.css";

// The top of the fan page: the share chip, the one close / back arrow (tip,
// playlist, song checkout), the DJ's bio, photo, Instagram, venue and name,
// the Tip Me chip, and the offline banner. Split out of pages/RequestPage.tsx.

type Props = {
  displayDj: any;
  viewDj: any;
  isProfilePreview: boolean;
  shareCopied: boolean;
  handleShare: () => Promise<void>;
  avatarImgSrc: (u: string) => string;
  tip: TipCheckout;
  buy: BuyCheckout;
  song: SongRequest;
};

export default function FanPageHeader({ displayDj, viewDj, isProfilePreview, shareCopied, handleShare, avatarImgSrc, tip, buy, song }: Props) {
  const { tipMode, setTipMode, setTipAmount, setTipPadOpen, setTipDraft, setTipCtaTapped, setTipClientSecret, setTipOrderId, setTipShowPay, setTipShowCard, setTipPayError, tipTiles } = tip;
  const { buyMode, closeBuyMode } = buy;
  const { selectedTrack, clearSelectedSong } = song;
  const navigate = useNavigate();
  // The talent's name is ONE line for fans, always (founder, 1.5.2 b48): it
  // shrinks until it fits. Real 30-character names (the limit) fit a 320-wide
  // phone at ~15 px; the floor is low enough that even 30 × "W" fits on one
  // line. Only an older, longer name can end in "…".
  const nameRef = useRef<HTMLDivElement>(null);
  useFitText(nameRef, `${displayDj.name}|${displayDj.isAcceptingRequests}`, 7);
  // Only fans who came from inside our own pages (Browse, the carousel)
  // carry this; a QR or link arrival has nowhere to go back to and must
  // never see an exit (founder, 1.4.1: drunk-proof). Router state rides
  // the history entry, so it survives picking a song and coming back.
  const cameFromBrowse = Boolean((useLocation().state as { fromBrowse?: boolean } | null)?.fromBrowse);
  return (
    <>
      {/* Landing state (no song, tip or playlist open): a back arrow, one
          step back to where the fan came from. Same spot and shape as the
          mode arrows below; the "⋯" menu is its twin on the right. */}
      {cameFromBrowse && !selectedTrack && !tipMode && !buyMode && !isProfilePreview && (
        <button
          type="button"
          className="rq-tip-close"
          onClick={() => navigate(-1)}
          aria-label={t("fanPageHeader.backToBrowse")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="14.5 5.5 8 12 14.5 18.5" />
          </svg>
        </button>
      )}
      {/* "⋯" menu — anchored to the card's top-right corner itself (the
          paper-plane share pill's old spot, founder screenshot 2026-08-04),
          so content flow inside the card never moves it. Share lives
          inside it now, with How requests work and Help & support
          (founder, 1.4.1). */}
      <FanPageMenu
        onShare={handleShare}
        shareCopied={shareCopied}
        djName={displayDj.name || "the DJ"}
        currency={displayDj.currency}
      />
      {/* Save this talent to Browse's "Saved" row (1.5.3) — the "⋯"'s twin,
          right under it. Landing state only: a checkout in progress gets no
          extra buttons. The talent's own Preview shows it, untappable. */}
      {!selectedTrack && !tipMode && !buyMode && (
        <SaveTalentHeart talent={displayDj} disabled={isProfilePreview} />
      )}
      {tipMode && !isProfilePreview && (
        <button
          type="button"
          className="rq-tip-close"
          onClick={() => {
            setTipMode(false);
            setTipPadOpen(false);
            setTipCtaTapped(false);
            setTipShowPay(false);
            setTipShowCard(false);
            setTipPayError(null);
            window.scrollTo(0, 0);
          }}
          aria-label={t("fanPageHeader.closeTip")}
        >
          ✕
        </button>
      )}
      {buyMode && !isProfilePreview && (
        // Sleek back arrow, not an ✕ (founder decision) — matches the song
        // checkout's arrow below.
        <button
          type="button"
          className="rq-tip-close"
          onClick={closeBuyMode}
          aria-label={t("fanPageHeader.back")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="14.5 5.5 8 12 14.5 18.5" />
          </svg>
        </button>
      )}
      {selectedTrack && !tipMode && !buyMode && (
        // Song checkout — the same back arrow in the same spot, replacing
        // the old × on the Selected Song pill (founder: "matching").
        <button
          type="button"
          className="rq-tip-close"
          onClick={clearSelectedSong}
          aria-label={t("fanPageHeader.backToSongSearch")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="14.5 5.5 8 12 14.5 18.5" />
          </svg>
        </button>
      )}
      {/* The preview banner moved OUTSIDE the embed — ProfilePage renders
          it under "Back to my profile" (founder 8/24), so the embedded
          page is exactly what fans see. */}
      <div className="rq-brand">
        {displayDj.bio && (
          <div className="rq-bio-thought" role="note">
            {displayDj.bio}
          </div>
        )}
        <div className="rq-logo-wrap">
          {displayDj.avatarUrl ? (
            <img
              src={avatarImgSrc(displayDj.avatarUrl)}
              alt={displayDj.name}
              className="rq-logo"
              loading="lazy"
              onError={showDefaultAvatar}
            />
          ) : (
            // No photo: the drawn default picture (1.5.2), not an initial.
            <img src={DEFAULT_AVATAR_SRC} alt={displayDj.name} className="rq-logo" />
          )}
          {/* IG lives on the photo's edge (founder decision) — same
              treatment as the camera badge on the DJ's own profile photo.
              Frees the title row for name + Tip Me + Share. */}
          {displayDj.instagram && (
            <a
              href={`https://instagram.com/${displayDj.instagram}`}
              className="rq-ig rq-ig--badge"
              title={`@${displayDj.instagram}`}
              aria-label={t("fanPageHeader.instagram")}
              onClick={(e) => {
                e.preventDefault();
                openExternal(`https://instagram.com/${displayDj.instagram}`);
              }}
            >
              <FaInstagram size={16} />
            </a>
          )}
        </div>

        {/* Venue ABOVE the name (founder pick 2B) — the event-
            ticket order: the little "where" line first, the headliner big
            beneath it. One spot; the DJ-side Preview embeds this same
            header so it follows automatically. */}
        {displayDj.venueName && (
          <div className="rq-venue-line" aria-label={`Venue: ${displayDj.venueName}`}>
            <svg
              className="rq-venue-pin"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{displayDj.venueName}</span>
          </div>
        )}

        <div className="rq-brand-title-row">
          <div className="rq-name" ref={nameRef} title={displayDj.name}>
            {displayDj.name}
            {displayDj.isAcceptingRequests === false && (
              <span style={{
                fontSize: "0.6rem", fontWeight: 600, color: "#9ca3af", background: "rgba(107,114,128,0.15)",
                padding: "2px 8px", borderRadius: 20, marginLeft: 8, verticalAlign: "middle", letterSpacing: "0.04em",
              }}>{t("fanPageHeader.offline")}</span>
            )}
          </div>

          {/* Free tier: a talent who can't take money yet has no Tip Me. */}
          {!tipMode && !buyMode && takesMoney(displayDj) && (
            <button
              type="button"
              className="rq-tip-chip"
              disabled={isProfilePreview}
              title={isProfilePreview ? t("fanPageHeader.availableOnYourLive") : undefined}
              onClick={() => {
                if (isProfilePreview) return;
                closeBuyMode();
                setTipMode(true);
                // Default = first tile (the currency's own first chip —
                // $5 in USD, exactly the old default).
                setTipAmount(String(tipTiles[0]?.amount ?? defaultPriceFor(viewDj?.currency)));
                setTipPadOpen(false);
                setTipDraft("");
                setTipCtaTapped(false);
                setTipClientSecret(null);
                setTipOrderId(null);
                setTipShowPay(false);
                setTipShowCard(false);
                setTipPayError(null);
              }}
              aria-label={t("fanPageHeader.sendTipOnly")}
            >
              {t("fanPageHeader.tipMe")}
            </button>
          )}
        </div>

        {/* Venue moved ABOVE the name row (2B) — see the block before the
            title row. The hero Playlist pill retired (founder 8/24 night);
            the two-product menu below is the entry now. */}

        {/* Price + Fast Pass rows removed (founder r3): the price
            lives in the "Request a Song $X" heading, and Fast Pass
            reveals itself in the bid box — the ⚡ "will be applied" pill
            once the bid crosses the threshold, plus the (i) explainer. */}

      </div>

      {displayDj?.isAcceptingRequests === false && (
        <div className="rq-offline-banner" role="status">
          <T k="fanPageHeader.nameIsOffline" vars={{ name: displayDj.name ?? "" }} tags={{ 0: (c) => <strong>{c}</strong> }} />
        </div>
      )}
    </>
  );
}
